import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Avatar,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  School as SchoolIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  PersonSearch as PersonSearchIcon,
} from '@mui/icons-material';
import { getDashboardOverview } from '../../api/statisticsApi';
import { getPartnerAdminStatistics } from '../../api/adminApi';
import { useAdmin } from '../../contexts/AdminContext';

const StatCard = ({ title, value, subtitle, icon: Icon, color, trend }) => (
  <Card
    sx={{
      height: '100%',
      borderRadius: 3,
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 3,
      },
    }}
  >
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Avatar
          sx={{
            backgroundColor: `${color}.light`,
            color: `${color}.dark`,
            width: 56,
            height: 56,
          }}
        >
          <Icon sx={{ fontSize: 28 }} />
        </Avatar>
        {trend && (
          <Chip
            icon={<TrendingUpIcon sx={{ fontSize: 16 }} />}
            label={trend}
            color="success"
            size="small"
            sx={{ fontWeight: 600 }}
          />
        )}
      </Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 0.5, color: 'text.primary' }}>
        {value}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mb: 1 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {subtitle}
        </Typography>
      )}
    </CardContent>
  </Card>
);

// Dashboard for Platform Admins — system-wide stats
const PlatformAdminDashboard = ({ stats }) => (
  <>
    <Grid container spacing={3} sx={{ mb: 4 }}>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Total Users"
          value={stats.users.total.toLocaleString()}
          subtitle={`${stats.users.active} active, ${stats.users.inactive} inactive`}
          icon={PeopleIcon}
          color="primary"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Total Registrations"
          value={stats.registrations.total.toLocaleString()}
          subtitle="Across all products"
          icon={AssignmentIcon}
          color="info"
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <StatCard
          title="Total Exams"
          value={stats.exams.total}
          subtitle={`${stats.exams.active} active, ${stats.exams.completed} completed`}
          icon={SchoolIcon}
          color="warning"
        />
      </Grid>
    </Grid>

    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card sx={{ borderRadius: 3, height: '100%' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              User Breakdown
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Active Users</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {stats.users.active} ({Math.round((stats.users.active / stats.users.total) * 100)}%)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(stats.users.active / stats.users.total) * 100}
                sx={{ height: 8, borderRadius: 4, backgroundColor: 'grey.200' }}
              />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Inactive Users</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {stats.users.inactive} ({Math.round((stats.users.inactive / stats.users.total) * 100)}%)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(stats.users.inactive / stats.users.total) * 100}
                sx={{ height: 8, borderRadius: 4, backgroundColor: 'grey.200' }}
                color="warning"
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card sx={{ borderRadius: 3, height: '100%' }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Exams Breakdown
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Active Exams</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {stats.exams.active} ({Math.round((stats.exams.active / stats.exams.total) * 100)}%)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(stats.exams.active / stats.exams.total) * 100}
                sx={{ height: 8, borderRadius: 4, backgroundColor: 'grey.200' }}
                color="success"
              />
            </Box>
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">Completed Exams</Typography>
                <Typography variant="body2" fontWeight={600}>
                  {stats.exams.completed} ({Math.round((stats.exams.completed / stats.exams.total) * 100)}%)
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(stats.exams.completed / stats.exams.total) * 100}
                sx={{ height: 8, borderRadius: 4, backgroundColor: 'grey.200' }}
                color="info"
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12}>
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
              Quick Statistics
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h3" color="primary.main" fontWeight={700}>
                    {stats.users.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Total Users
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h3" color="info.main" fontWeight={700}>
                    {stats.registrations.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Total Registrations
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="h3" color="warning.main" fontWeight={700}>
                    {stats.exams.active}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Active Exams
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  </>
);

// Dashboard for Partner Admins — scoped to their partner's data
const PartnerAdminDashboard = ({ stats }) => {
  const { summary, partnerBreakdown } = stats;
  const classifiedPct = summary.totalStudents > 0
    ? Math.round((summary.classifiedStudents / summary.totalStudents) * 100)
    : 0;

  return (
    <>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Students"
            value={summary.totalStudents.toLocaleString()}
            subtitle={`${summary.internalStudents} internal, ${summary.externalStudents} external`}
            icon={PeopleIcon}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Registrations"
            value={summary.totalRegistrations.toLocaleString()}
            subtitle="From your partner's students"
            icon={AssignmentIcon}
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Completed Exams"
            value={summary.completedExams.toLocaleString()}
            subtitle="Exams with scores"
            icon={CheckCircleIcon}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Classified Students"
            value={summary.classifiedStudents.toLocaleString()}
            subtitle={`${summary.unclassifiedStudents} unclassified`}
            icon={PersonSearchIcon}
            color="warning"
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Student Classification
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Classified</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {summary.classifiedStudents} ({classifiedPct}%)
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={classifiedPct}
                  sx={{ height: 8, borderRadius: 4, backgroundColor: 'grey.200' }}
                  color="success"
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Internal</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {summary.internalStudents}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={summary.totalStudents > 0 ? (summary.internalStudents / summary.totalStudents) * 100 : 0}
                  sx={{ height: 8, borderRadius: 4, backgroundColor: 'grey.200' }}
                  color="primary"
                />
              </Box>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">External</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {summary.externalStudents}
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={summary.totalStudents > 0 ? (summary.externalStudents / summary.totalStudents) * 100 : 0}
                  sx={{ height: 8, borderRadius: 4, backgroundColor: 'grey.200' }}
                  color="warning"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ borderRadius: 3, height: '100%' }}>
            <CardContent>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Quick Statistics
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h3" color="primary.main" fontWeight={700}>
                      {summary.totalStudents}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Total Students
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h3" color="info.main" fontWeight={700}>
                      {summary.totalRegistrations}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Registrations
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h3" color="success.main" fontWeight={700}>
                      {summary.completedExams}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Completed Exams
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="h3" color="warning.main" fontWeight={700}>
                      {classifiedPct}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Classification Rate
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
};

const AdminDashboard = () => {
  const { isPlatformAdmin } = useAdmin();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data } = isPlatformAdmin
          ? await getDashboardOverview()
          : await getPartnerAdminStatistics();
        setStats(isPlatformAdmin ? data.data : data);
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, [isPlatformAdmin]);

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 4 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (!stats) {
    return (
      <Box sx={{ textAlign: 'center', mt: 8 }}>
        <Typography variant="h6" color="text.secondary">
          Failed to load dashboard statistics
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ backgroundColor: 'primary.main', width: 48, height: 48, mr: 2 }}>
            <DashboardIcon />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {isPlatformAdmin ? 'Admin Dashboard' : 'Partner Dashboard'}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {isPlatformAdmin
                ? 'Overview of your IELTS platform statistics'
                : 'Overview of your partner statistics'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {isPlatformAdmin
        ? <PlatformAdminDashboard stats={stats} />
        : <PartnerAdminDashboard stats={stats} />
      }
    </Box>
  );
};

export default AdminDashboard;
